import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  buttonVariants,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
} from "@jem-open/jem-ui";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  MapPin,
  MoreVertical,
  Plus,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../../themes/components/PageContainer";
import useSitesAndRegions from "../hooks/useSitesAndRegions";
import CreateRegionDialog from "../components/CreateRegionDialog";
import CreateSiteDialog from "../components/CreateSiteDialog";
import MoveSiteDialog from "../components/MoveSiteDialog";
import MoveRosterDialog from "../components/MoveRosterDialog";

const SitesAndRegionsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    regions,
    isLoading,
    isError,
    createRegionOpen,
    setCreateRegionOpen,
    newRegionName,
    setNewRegionName,
    confirmCreateRegion,
    createSiteOpen,
    openCreateSite,
    closeCreateSite,
    newSiteRegionId,
    setNewSiteRegionId,
    newSiteName,
    setNewSiteName,
    confirmCreateSite,
    pendingMoveSite,
    setPendingMoveSite,
    moveSiteDestination,
    setMoveSiteDestination,
    confirmMoveSite,
    pendingMoveRoster,
    setPendingMoveRoster,
    moveRosterDestination,
    setMoveRosterDestination,
    confirmMoveRoster,
    successAlert,
    setSuccessAlert,
    allSites,
    regionRosterCount,
  } = useSitesAndRegions();

  const totalSites = regions.reduce((acc, r) => acc + r.sites.length, 0);
  const totalRosters = regions.reduce(
    (acc, r) => acc + regionRosterCount(r),
    0
  );

  // The synthetic "ungrouped" bucket is not a real Region — Sites cannot be
  // created into it, so it is filtered out of the Create Site Region picker.
  const creatableRegions = regions.filter((r) => r.id !== "ungrouped");

  const moveSiteName = pendingMoveSite
    ? regions
        .find((r) => r.id === pendingMoveSite.fromRegionId)
        ?.sites.find((s) => s.id === pendingMoveSite.siteId)?.name ?? null
    : null;
  const moveSiteDestinationRegions = pendingMoveSite
    ? regions.filter((r) => r.id !== pendingMoveSite.fromRegionId)
    : regions;

  const moveRosterFromSite = pendingMoveRoster
    ? regions
        .flatMap((r) => r.sites)
        .find((s) => s.id === pendingMoveRoster.fromSiteId)
    : undefined;
  const moveRosterName =
    moveRosterFromSite?.rosters.find(
      (rr) => rr.id === pendingMoveRoster?.rosterId
    )?.name ?? null;
  const moveRosterDestinationSites = allSites.filter(
    (s) => s.id !== pendingMoveRoster?.fromSiteId
  );

  return (
    <PageContainer>
      <div
        className="-mx-[40px] -mt-[32px] px-[40px] py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
          Sites &amp; Regions
        </h1>
        <p className="mt-xxs text-sm text-greyscale-text-body max-w-[760px]">
          Browse the hierarchy of Regions, Sites, and Rosters. Create new
          Regions and Sites, and move Sites between Regions.
        </p>
      </div>

      <div className="space-y-md max-w-[1100px] pb-[120px]">
        {/* Top action bar */}
        <div className="flex flex-wrap items-center justify-between gap-sm">
          <p className="text-sm text-greyscale-text-body">
            <strong>{regions.length}</strong> Region
            {regions.length === 1 ? "" : "s"}
            <span className="mx-xs text-greyscale-text-caption">·</span>
            <strong>{totalSites}</strong> Sites
            <span className="mx-xs text-greyscale-text-caption">·</span>
            <strong>{totalRosters}</strong> Rosters
          </p>
          <DropdownMenu>
            {/*
             * jem-ui's Button is not a forwardRef component, so it cannot be
             * used as an `asChild` trigger child — Radix drops the anchor ref
             * and the menu renders off-screen. Style the Radix trigger (which
             * renders its own <button>) directly with buttonVariants instead.
             */}
            <DropdownMenuTrigger
              className={cn(buttonVariants({ variant: "default", size: "sm" }))}
            >
              <Plus size={14} />
              Create
              <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCreateRegionOpen(true)}>
                New Region
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openCreateSite()}>
                New Site
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Success Alert */}
        {successAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setSuccessAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              {successAlert.title}
            </AlertTitle>
            <AlertDescription>
              <p className="pr-lg">{successAlert.body}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-md">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        )}

        {/* Error state */}
        {!isLoading && isError && (
          <Alert variant="error">
            <AlertTitle>Couldn&apos;t load Sites &amp; Regions</AlertTitle>
            <AlertDescription>
              <p>
                Something went wrong loading the configuration. Please try
                again.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Regions accordion */}
        {!isLoading && !isError && (
          <Accordion
            type="multiple"
            defaultValue={regions.map((r) => r.id)}
            className="space-y-md"
          >
            {regions.map((region) => {
              const regionTotalRosters = regionRosterCount(region);
              return (
                <AccordionItem
                  key={region.id}
                  value={region.id}
                  className="overflow-hidden rounded-lg border border-greyscale-border bg-white last:border-b"
                >
                  <AccordionTrigger className="!px-10 py-md hover:no-underline">
                    <div className="text-left">
                      <h2 className="font-heading text-lg font-semibold text-greyscale-text-title">
                        {region.name}
                      </h2>
                      <p className="text-xs text-greyscale-text-caption">
                        <strong className="text-greyscale-text-title">
                          {region.sites.length}
                        </strong>{" "}
                        Site{region.sites.length === 1 ? "" : "s"}
                        <span className="mx-xxs">·</span>
                        <strong className="text-greyscale-text-title">
                          {regionTotalRosters}
                        </strong>{" "}
                        Roster{regionTotalRosters === 1 ? "" : "s"}
                      </p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="border-t border-greyscale-border">
                      {region.sites.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-xs p-xl text-center">
                          <p className="text-sm text-greyscale-text-caption">
                            No Sites in this Region yet.
                          </p>
                          <Button
                            variant="default"
                            size="sm"
                            leftIcon={<Plus size={14} />}
                            onClick={() => openCreateSite(region.id)}
                          >
                            Add the first Site
                          </Button>
                        </div>
                      ) : (
                        <ul className="divide-y divide-greyscale-border">
                          {region.sites.map((site) => (
                            <li
                              key={site.id}
                              className="flex flex-wrap items-start justify-between gap-md p-md"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-greyscale-text-title">
                                  {site.name}
                                </p>
                                <p className="mt-xxxs flex items-center gap-xxs text-xs text-greyscale-text-caption">
                                  <MapPin size={12} aria-hidden="true" />
                                  {site.addressPreview}
                                </p>
                                {site.rosters.length > 0 && (
                                  <ul className="mt-xs space-y-xxs">
                                    {site.rosters.map((roster) => (
                                      <li
                                        key={roster.id}
                                        className="flex flex-wrap items-center justify-between gap-xs"
                                      >
                                        <span className="text-xs text-greyscale-text-body">
                                          <strong>{roster.name}</strong>{" "}
                                          <span className="text-greyscale-text-caption">
                                            ({roster.shiftLabel} ·{" "}
                                            {roster.assigned} assigned)
                                          </span>
                                        </span>
                                        <DropdownMenu>
                                          <DropdownMenuTrigger
                                            className={cn(
                                              buttonVariants({
                                                variant: "ghost",
                                                size: "icon",
                                              }),
                                              "h-7 w-7"
                                            )}
                                            aria-label={`Actions for ${roster.name}`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <MoreVertical size={14} />
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent
                                            align="end"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <DropdownMenuItem
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPendingMoveRoster({
                                                  rosterId: roster.id,
                                                  fromSiteId: site.id,
                                                });
                                              }}
                                            >
                                              Move to Site…
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {site.rosters.length === 0 && (
                                  <p className="mt-xs text-xs text-greyscale-text-caption">
                                    No Rosters on this Site yet.
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-shrink-0 items-center gap-xs">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setPendingMoveSite({
                                      siteId: site.id,
                                      fromRegionId: region.id,
                                    })
                                  }
                                >
                                  Move Site
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  rightIcon={<ChevronRight size={14} />}
                                  onClick={() =>
                                    navigate(`/site-configuration/${site.id}`)
                                  }
                                >
                                  Configure
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      <CreateRegionDialog
        open={createRegionOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateRegionOpen(false);
            setNewRegionName("");
          }
        }}
        name={newRegionName}
        onNameChange={setNewRegionName}
        onConfirm={confirmCreateRegion}
      />

      <CreateSiteDialog
        open={createSiteOpen}
        onOpenChange={(open) => {
          if (!open) closeCreateSite();
        }}
        regions={creatableRegions}
        regionId={newSiteRegionId}
        onRegionIdChange={setNewSiteRegionId}
        name={newSiteName}
        onNameChange={setNewSiteName}
        onConfirm={confirmCreateSite}
      />

      <MoveSiteDialog
        open={pendingMoveSite !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingMoveSite(null);
            setMoveSiteDestination("");
          }
        }}
        siteName={moveSiteName}
        destinationRegions={moveSiteDestinationRegions}
        destination={moveSiteDestination}
        onDestinationChange={setMoveSiteDestination}
        onConfirm={confirmMoveSite}
      />

      <MoveRosterDialog
        open={pendingMoveRoster !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingMoveRoster(null);
            setMoveRosterDestination("");
          }
        }}
        rosterName={moveRosterName}
        sourceSiteName={moveRosterFromSite?.name ?? ""}
        destinationSites={moveRosterDestinationSites}
        destination={moveRosterDestination}
        onDestinationChange={setMoveRosterDestination}
        onConfirm={confirmMoveRoster}
      />
    </PageContainer>
  );
};

export default SitesAndRegionsPage;
